import React, { SetStateAction, Dispatch, useRef, useState, useEffect } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { getDealers } from 'src/api/userManagement';
import { getCustomersByPermission } from 'src/api/customer';
import './PermissionsFlow.scss';
import {
    EntityAccess,
    EntityType,
    Permissions,
    UserPermission,
    PermissionContext,
    ShippingPortalLocation,
    LocationInfo,
    FmtpCustomer,
    CustomerInfo,
} from '@lanterds/types';
import {
    HierarchyCustomer,
    SelectedPermission,
    DealerHierarchyItem,
    SelectedDealer,
    SelectedCustomer,
    Quantity,
    FetchedUserPermissions,
} from '../types';
import { getCustomerInfoHierarchical, getHasAllCustomersAccess } from 'src/util/helpers';
import PermissionsFlowTile from './PermissionsFlowTile';
import CustomersFlowTile from './CustomersFlowTile';
import DealersFlowTile from './DealersFlowTile';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';

type PermissionsFlowProps = {
    permissionsOptions: SelectedPermission[];
    fetchedUserPermissions: FetchedUserPermissions;
    saveEnabled?: boolean;
    cancelNavigatePage?: string;
    cancelEnabled?: boolean;
    selectedPermissions: SelectedPermission[];
    setSelectedPermissions: Dispatch<SetStateAction<SelectedPermission[]>>;
    isUserPermissionsFetching?: boolean;
    adminsPermissions: Partial<Record<Permissions, boolean | EntityAccess[]>>;
    canGetCustomers: boolean;
    onAddedPermissions: ({
        userPermissions,
        customerAccessList,
        dealerAccessList,
        changedPermissions,
    }: {
        userPermissions?: UserPermission;
        customerAccessList?: FmtpCustomer[];
        dealerAccessList?: LocationInfo[];
        changedPermissions?: SelectedPermission[];
    }) => void;
};

const ALL_DEALERS_HIERARCHY_ITEM = {
    name: Quantity.all,
    hierarchyId: Quantity.all,
    displayName: 'All Dealers',
    id: Quantity.all,
    locationCode: Quantity.all,
    city: Quantity.all,
    state: Quantity.all,
    street: Quantity.all,
    zip: Quantity.all,
    fmtpCustomerId: Quantity.all,
};

export default function PermissionsFlow({
    permissionsOptions,
    fetchedUserPermissions,
    saveEnabled = true,
    cancelNavigatePage,
    selectedPermissions,
    setSelectedPermissions,
    isUserPermissionsFetching,
    adminsPermissions,
    canGetCustomers,
    onAddedPermissions,
}: PermissionsFlowProps) {
    const containerRef = useRef<HTMLElement>(null);
    const fmtpNavigate = useFmtpNavigate();

    const [selectedCustomers, setSelectedCustomers] = useState<SelectedCustomer[]>([]);
    const [selectedDealers, setSelectedDealers] = useState<SelectedDealer[]>([]);
    const [customersHierarchy, setCustomersHierarchy] = useState<HierarchyCustomer[]>([]);
    const [dealers, setDealers] = useState<DealerHierarchyItem[]>([]);
    const [fetchedCustomerData, setFetchedCustomerData] = useState<CustomerInfo[]>([]);
    const [customersAreLoading, setCustomersAreLoading] = useState(false);

    const {
        data: fetchedDealers,
        isFetching: isDealersFetching,
        isError: isDealersError,
    } = getDealers(
        selectedCustomers,
        !!(selectedCustomers.length && !selectedCustomers.find((customer) => customer.customerCode === Quantity.all)),
    );

    useEffect(() => {
        if (fetchedDealers && fetchedDealers.length && !isDealersFetching) {
            if (isDealersError) {
                setDealers([]);
            } else {
                const dealersList = [];
                if (selectedCustomers[0].type !== EntityType.ACCESS_ALL_CUSTOMERS) {
                    dealersList.push(ALL_DEALERS_HIERARCHY_ITEM);
                }
                setDealers([
                    ...dealersList,
                    ...(
                        selectedCustomers.map((customer, index) => ({
                            displayName: customer.customerName,
                            name: customer.customerName,
                            hierarchyId: customer.customerId,
                            children: createDealersListFromCustomer(fetchedDealers, customer, index),
                        })) as DealerHierarchyItem[]
                    ).sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())),
                ]);
            }
        }
    }, [fetchedDealers, isDealersFetching, isDealersError]);

    useEffect(() => {
        if (canGetCustomers) {
            setCustomersAreLoading(true);
            getCustomersByPermission(Permissions.userManagement).then((data: CustomerInfo[]) => {
                const customerHierarchyData = getCustomerInfoHierarchical(data, getHasAllCustomersAccess(adminsPermissions));

                setFetchedCustomerData(data);
                setCustomersHierarchy(customerHierarchyData);

                setCustomersAreLoading(false);
            });
        }
    }, [canGetCustomers]);

    const createCustomerPermissions = (
        selectedPermission: SelectedPermission,
        userPermissions: UserPermission,
        tempCustomerAccessList: FmtpCustomer[],
    ) => {
        const newUserPermissions = { ...userPermissions };

        if (selectedPermission.name in userPermissions) {
            const userEntityAccessList = newUserPermissions[selectedPermission.name];
            let permissionAlreadyAddedToUser = undefined;

            if (Array.isArray(userEntityAccessList)) {
                permissionAlreadyAddedToUser = userEntityAccessList.find((userEntity) => {
                    if (userEntity.type === EntityType.ACCESS_ALL_CUSTOMERS) {
                        return selectedCustomers[0].type === EntityType.ACCESS_ALL_CUSTOMERS;
                    } else {
                        return !!selectedCustomers.find((customer) => customer.customerId === userEntity.id);
                    }
                });
            }

            // Skip any permissions that are already in the User's permissions
            if (!permissionAlreadyAddedToUser) {
                let entities = newUserPermissions[selectedPermission.name];
                if (selectedCustomers[0].type === EntityType.ACCESS_ALL_CUSTOMERS) {
                    entities = [{ type: EntityType.ACCESS_ALL_CUSTOMERS }];
                } else {
                    // Need to check here since entities can be undefined
                    if (entities) {
                        if (entities === true) {
                            entities = true;
                        } else {
                            for (const selectedCustomer of selectedCustomers) {
                                entities.push({ id: selectedCustomer.id, type: selectedCustomer.type });
                                tempCustomerAccessList.push({
                                    ...selectedCustomer,
                                    fmtpId: selectedCustomer.customerId,
                                    name: selectedCustomer.customerName,
                                });
                            }
                        }
                    }
                }
                newUserPermissions[selectedPermission.name] = entities;
            }
        } else {
            newUserPermissions[selectedPermission.name] = selectedCustomers.map((customer) => {
                tempCustomerAccessList.push({
                    ...customer,
                    fmtpId: customer.customerId,
                    name: customer.customerName,
                });
                return {
                    id: customer.id,
                    type: customer.type,
                };
            });
        }

        return newUserPermissions;
    };

    const savePermissionsHandler = async () => {
        let userPermissions = fetchedUserPermissions?.permissions ? { ...fetchedUserPermissions.permissions } : {};
        const tempCustomerAccessList: FmtpCustomer[] = [];
        const tempDealerAccessList: LocationInfo[] = [];

        switch (selectedPermissions[0].context) {
            case PermissionContext.GLOBAL:
                for (const permission of selectedPermissions) {
                    if (!(permission.name in userPermissions)) {
                        userPermissions[permission.name] = [
                            {
                                type: EntityType.ACCESS_ALL_CUSTOMERS,
                            },
                        ];
                    }
                }
                break;
            case PermissionContext.CUSTOMER:
                for (const permission of selectedPermissions) {
                    userPermissions = createCustomerPermissions(permission, userPermissions, tempCustomerAccessList);
                }
                break;
            case PermissionContext.DEALER:
                for (const permission of selectedPermissions) {
                    if (selectedCustomers[0].type === EntityType.ACCESS_ALL_CUSTOMERS) {
                        userPermissions[permission.name] = [{ type: EntityType.ACCESS_ALL_CUSTOMERS }];
                    } else if (selectedDealers[0].id === Quantity.all) {
                        const entities = userPermissions[permission.name];

                        if (entities && Array.isArray(entities)) {
                            const filteredEntities = entities.filter((entity) => {
                                if (entity.type === EntityType.DEALER) {
                                    const foundDealer = fetchedUserPermissions?.dealerAccessList?.find(
                                        (dealer) => dealer.accountId === entity.id,
                                    );
                                    const foundCustomer = selectedCustomers.find(
                                        (customer) => customer.customerId === foundDealer?.fmtpCustomerId,
                                    );
                                    if (foundCustomer) {
                                        return false;
                                    }
                                }

                                return true;
                            });

                            userPermissions[permission.name] = filteredEntities;
                        }

                        userPermissions = createCustomerPermissions(permission, userPermissions, tempCustomerAccessList);
                    } else {
                        // Handle individual dealer selections
                        if (permission.name in userPermissions) {
                            const userEntityAccessList = userPermissions[permission.name];
                            let permissionAlreadyAddedToUser = undefined;

                            if (Array.isArray(userEntityAccessList)) {
                                permissionAlreadyAddedToUser = userEntityAccessList.find((userEntity) => {
                                    if (userEntity.type === EntityType.ACCESS_ALL_CUSTOMERS) {
                                        return selectedDealers[0].type === EntityType.ACCESS_ALL_CUSTOMERS;
                                    } else {
                                        return !!selectedDealers.find((dealer) => dealer.id === userEntity.id);
                                    }
                                });
                            }

                            // Skip any permissions that are already in the User's permissions
                            if (!permissionAlreadyAddedToUser) {
                                let entities = userPermissions[permission.name];
                                if (selectedDealers[0].type === EntityType.ACCESS_ALL_CUSTOMERS) {
                                    entities = [{ type: EntityType.ACCESS_ALL_CUSTOMERS }];
                                } else {
                                    // Need to check here since entities can be undefined
                                    if (entities) {
                                        if (entities === true) {
                                            entities = true;
                                        } else {
                                            for (const selectedDealer of selectedDealers) {
                                                entities.push({ id: selectedDealer.id, type: selectedDealer.type });
                                                tempDealerAccessList.push({
                                                    ...selectedDealer,
                                                    accountId: selectedDealer.id,
                                                    parentAccountId: selectedDealer.fmtpCustomerId,
                                                    addressLine1: selectedDealer.street,
                                                    addressZip: selectedDealer.zip,
                                                    addressCity: selectedDealer.city,
                                                    addressState: selectedDealer.state,
                                                    locationType: selectedDealer.type,
                                                });

                                                const tempCustomer = selectedCustomers.find(
                                                    (customer) => customer.customerId === selectedDealer.fmtpCustomerId,
                                                );

                                                tempCustomerAccessList.push({
                                                    fmtpId: selectedDealer.fmtpCustomerId,
                                                    name: tempCustomer ? tempCustomer.customerName : '',
                                                });
                                            }
                                        }
                                    }
                                }
                                userPermissions[permission.name] = entities;
                            }
                        } else {
                            userPermissions[permission.name] = selectedDealers.map((dealer) => {
                                tempDealerAccessList.push({
                                    ...dealer,
                                    accountId: dealer.id,
                                    parentAccountId: dealer.fmtpCustomerId,
                                    addressLine1: dealer.street,
                                    addressZip: dealer.zip,
                                    addressCity: dealer.city,
                                    addressState: dealer.state,
                                    locationType: dealer.type,
                                });

                                const tempCustomer = selectedCustomers.find(
                                    (customer) => customer.customerId === dealer.fmtpCustomerId,
                                );

                                tempCustomerAccessList.push({
                                    fmtpId: dealer.fmtpCustomerId,
                                    name: tempCustomer ? tempCustomer.customerName : '',
                                });

                                return {
                                    id: dealer.id,
                                    type: dealer.type,
                                };
                            });
                        }
                    }
                }

                break;
            default:
                break;
        }

        setSelectedCustomers([]);
        setSelectedDealers([]);
        setDealers([]);
        setSelectedPermissions([]);
        if (onAddedPermissions)
            onAddedPermissions({
                userPermissions,
                customerAccessList: tempCustomerAccessList,
                dealerAccessList: tempDealerAccessList,
                changedPermissions: selectedPermissions,
            });
    };

    const onClickCancel = () => {
        if (cancelNavigatePage) fmtpNavigate(cancelNavigatePage);
    };

    const getIsSavePermissionsButtonDisabled = () => {
        let disabled = false;

        switch (selectedPermissions[0]?.context) {
            case undefined:
                disabled = true;
                break;
            case PermissionContext.CUSTOMER:
                if (!selectedCustomers.length) {
                    disabled = true;
                }

                break;
            case PermissionContext.DEALER:
                if (
                    !selectedCustomers.length ||
                    (selectedCustomers[0].type !== EntityType.ACCESS_ALL_CUSTOMERS && !selectedDealers.length)
                ) {
                    disabled = true;
                }

                break;
            default:
                break;
        }

        return disabled;
    };

    const getPermissionsSaveButtonTooltip = () => {
        let label = '';

        switch (selectedPermissions[0]?.context) {
            case undefined:
                label = 'Please make Permission selections before saving.';
                break;
            case PermissionContext.CUSTOMER:
                if (!selectedCustomers.length) {
                    label = 'Please make Customer selections before saving.';
                }

                break;
            case PermissionContext.DEALER:
                if (!selectedDealers.length) {
                    label = 'Please make Dealer selections before saving.';
                }

                if (!selectedCustomers.length) {
                    label = 'Please make Customer selections before saving.';
                }

                break;
            default:
                break;
        }

        return label;
    };

    const createDealersListFromCustomer = (
        fetchedDealers: ShippingPortalLocation[][],
        customer: SelectedCustomer,
        index: number,
    ) => {
        if (fetchedDealers[index] && fetchedDealers[index].length) {
            return (
                fetchedDealers[index]
                    ?.map((dealer) => ({
                        ...dealer,
                        hierarchyId: `${customer.customerId}_${dealer.locationCode}_${dealer.id}`,
                        displayName: `${dealer.locationCode} - ${dealer.name}`,
                    }))
                    .sort((a, b) => a.locationCode.toLocaleLowerCase().localeCompare(b.locationCode.toLocaleLowerCase())) ?? []
            );
        }

        return [
            {
                displayName: 'No Dealers',
                name: Quantity.none,
                hierarchyId: `${customer.customerId}_NONE`,
            },
        ];
    };

    return (
        <>
            <Box id="flow-container">
                <PermissionsFlowTile
                    selectedPermissions={selectedPermissions}
                    setSelectedPermissions={setSelectedPermissions}
                    isUserPermissionsFetching={isUserPermissionsFetching}
                    permissionsOptions={permissionsOptions}
                    containerRef={containerRef}
                    setSelectedCustomers={setSelectedCustomers}
                    setSelectedDealers={setSelectedDealers}
                    setDealers={setDealers}
                />

                <CustomersFlowTile
                    selectedCustomers={selectedCustomers}
                    setSelectedCustomers={setSelectedCustomers}
                    isCustomersFetching={customersAreLoading}
                    containerRef={containerRef}
                    setSelectedDealers={setSelectedDealers}
                    setDealers={setDealers}
                    isVisible={
                        !!(
                            selectedPermissions.length &&
                            (selectedPermissions[0].context === PermissionContext.CUSTOMER ||
                                selectedPermissions[0].context === PermissionContext.DEALER)
                        )
                    }
                    customersHierarchy={customersHierarchy}
                    fetchedCustomerData={fetchedCustomerData}
                />

                <DealersFlowTile
                    setSelectedDealers={setSelectedDealers}
                    selectedDealers={selectedDealers}
                    dealers={dealers}
                    containerRef={containerRef}
                    isDealersFetching={isDealersFetching}
                    isVisible={
                        !!(
                            selectedCustomers.length &&
                            !selectedCustomers.find((customer) => customer.customerId === Quantity.all) &&
                            selectedPermissions.length &&
                            selectedPermissions[0].context === PermissionContext.DEALER
                        )
                    }
                    selectedCustomers={selectedCustomers}
                    adminsPermissions={adminsPermissions}
                />
            </Box>
            <Box id="button-container">
                {cancelNavigatePage && (
                    <Button size="medium" variant="contained" onClick={onClickCancel}>
                        Cancel
                    </Button>
                )}
                {saveEnabled && (
                    <Tooltip arrow title={getPermissionsSaveButtonTooltip()} placement="right">
                        {/* Need to wrap in block element for tooltip to work when button is disabled */}
                        <Box>
                            <Button
                                disabled={getIsSavePermissionsButtonDisabled()}
                                onClick={savePermissionsHandler}
                                variant="contained"
                                size="medium"
                                classes="button"
                            >
                                Add Permissions
                            </Button>
                        </Box>
                    </Tooltip>
                )}
            </Box>
        </>
    );
}
